'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { useDiscarded } from '@/components/sat/HideOnDiscard'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'

// "Discard this test and start over." An open SAT-plan attempt blocks starting
// ANY new test — /api/sat/start resumes any not-completed attempt before it
// honors the requested kind (an in-progress test must never be stranded). That
// is correct for someone who WANTS to resume, but it trapped a real user
// (meghnasankar@) who clicked "Start Checkpoint" repeatedly and kept landing in
// a three-week-old SAT with no way out except finishing ~3 modules. This is
// that escape hatch.
//
// POSTs /api/sat/discard (owner-verified server-side; hard-deletes the attempt
// + its AttemptItem/AttemptModule rows in a transaction, audits test.abandoned
// — so a discarded attempt leaves NOTHING behind to count as a baseline or as
// engagement). A confirm modal spells out that answers are lost — destructive
// and irreversible, so never a one-click action.
//
// The one thing a discard does NOT undo is a Benchmark sitting. The student has
// already seen the frozen paper, so the BenchmarkSitting row outlives the
// attempt and that form stays used up (src/lib/test-engine/benchmark-sittings.ts).
// `forfeitsBenchmark` puts that in the confirm, before the click, not after.
//
// The request names the attempt this button was rendered for, and says when
// the confirm carried that warning. The page decided both when it rendered, so
// a stale tab or a second device can be showing a test that is no longer the
// open one. The server then refuses (409) rather than delete a different test,
// or a Benchmark the student was never warned about.
//
// afterDiscard:
//  - { kind }  → immediately POST /api/sat/start with that kind and navigate
//                into the fresh test (the /checkpoint "discard & start" path).
//  - 'refresh' → just re-render the current page so the resume surface clears
//                (the dashboard card path — user picks their next test).
//  - { goto }  → navigate to that page (the Runner's timed-out prompt, where a
//                refresh would only reload the page of an attempt that no
//                longer exists).
export default function DiscardAttemptButton({
  attemptId,
  afterDiscard,
  label = 'Discard & start over',
  attemptLabel = 'test',
  variant = 'outline',
  className,
  forfeitsBenchmark = false,
  layerClassName,
  onOpenChange,
}: {
  /** The open attempt this button discards: the one the page is showing. */
  attemptId: string
  afterDiscard: { kind: 'checkpoint' | 'sat' } | { goto: string } | 'refresh'
  label?: string
  /** Human name of the attempt being discarded, for the modal copy. */
  attemptLabel?: string
  variant?: 'default' | 'outline' | 'ghost' | 'destructive' | 'secondary'
  className?: string
  /** The open attempt is a Benchmark: discarding it uses that form up (only staff can reset it). */
  forfeitsBenchmark?: boolean
  /**
   * A z-index class for the confirm (backdrop and panel), for a caller that
   * opens it from inside its own modal. The confirm portals to <body> at z-50,
   * so inside a higher modal it would open underneath it.
   */
  layerClassName?: string
  /** Told when the confirm opens or closes, e.g. to pause the caller's own focus trap. */
  onOpenChange?: (open: boolean) => void
}) {
  const router = useRouter()
  // Present only when the surface wrapped itself in <HideOnDiscard>.
  const discarded = useDiscarded()
  const [open, setOpenState] = useState(false)
  function setOpen(v: boolean) {
    setOpenState(v)
    onOpenChange?.(v)
  }
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function confirmDiscard() {
    setBusy(true)
    setError(null)
    try {
      const r = await fetch('/api/sat/discard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-store' },
        cache: 'no-store',
        body: JSON.stringify({ attemptId, forfeitBenchmark: forfeitsBenchmark }),
      })
      // 404 = nothing to discard (already gone) — treat as success, not error.
      if (!r.ok && r.status !== 404) {
        const data = (await r.json().catch(() => ({}))) as { error?: string; message?: string }
        throw new Error(data.message || data.error || 'Could not discard the test.')
      }

      if (afterDiscard === 'refresh') {
        setOpen(false)
        // Clear the card NOW. router.refresh() re-renders a whole server page
        // and the student was watching a stale "resume your test" card for the
        // whole round trip — offering to resume a test that no longer exists.
        discarded?.onDiscarded()
        router.refresh()
        return
      }

      if ('goto' in afterDiscard) {
        router.push(afterDiscard.goto)
        router.refresh()
        return
      }

      const r2 = await fetch('/api/sat/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-store' },
        cache: 'no-store',
        body: JSON.stringify({ kind: afterDiscard.kind }),
      })
      const data = await r2.json().catch(() => ({}) as { goto?: string; error?: string })
      if (!r2.ok) throw new Error(data?.error || 'Could not start the new test.')
      router.push(data.goto ?? '/sat/resume')
      router.refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.')
      setBusy(false)
    }
  }

  return (
    <>
      <Button
        type="button"
        variant={variant}
        className={className}
        onClick={() => {
          setError(null)
          setOpen(true)
        }}
      >
        {label}
      </Button>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className={layerClassName} overlayClassName={layerClassName}>
          <DialogHeader>
            <DialogTitle>Discard this {attemptLabel}?</DialogTitle>
            <DialogDescription>
              This permanently deletes your in-progress {attemptLabel} and every answer in it — this
              can&apos;t be undone.{' '}
              {afterDiscard === 'refresh' || 'goto' in afterDiscard
                ? 'Nothing starts in its place — you choose what to do next.'
                : "You'll start fresh."}
            </DialogDescription>
          </DialogHeader>
          {forfeitsBenchmark && (
            <p className="text-sm font-medium text-foreground">
              A Benchmark can be sat only once. Discarding it uses it up.
            </p>
          )}
          {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
              Keep it
            </Button>
            <Button variant="destructive" onClick={confirmDiscard} disabled={busy} aria-busy={busy}>
              {/* In refresh mode nothing starts, so the button must not say it
                  does — that promise is what made discarding feel like a loop. */}
              {busy
                ? 'Discarding…'
                : afterDiscard === 'refresh' || 'goto' in afterDiscard
                  ? 'Discard'
                  : 'Discard & start new'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
